// Schnellstart (M06, leerer Mandant): Branche wählen → Starter-Playlist aus 3–4 Branchenvorlagen,
// sofort veröffentlicht, damit in wenigen Minuten etwas Schönes auf dem Bildschirm läuft.
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api/client';
import { can, qk, useLayouts, useMe, useTemplateCatalog } from '../../api/queries';
import type { LayoutRow, TemplateCatalog, TemplateCategory } from '../../api/types';
import { Icon } from '../../components/Icon';
import { useMediaIndex, type MediaIndex } from '../../components/Preview';
import { useToast } from '../../components/toast';
import { Dialog } from '../../components/ui';
import './gallery.css';
import { TemplateFrame } from './TemplateDialogs';
import { tr } from './shared';

type Orientation = 'landscape' | 'portrait';

const starterLayouts = (catalog: TemplateCatalog, c: TemplateCategory, o: Orientation) =>
  c.starter.map((g) => catalog.items.find((i) => i.group === g && i.orientation === o)).filter((x) => !!x);

export function QuickstartCard() {
  const { t, i18n } = useTranslation();
  const { data: me } = useMe();
  const { data: catalog } = useTemplateCatalog();
  const { data: layouts } = useLayouts();
  const media = useMediaIndex();
  const [pick, setPick] = useState<TemplateCategory | null>(null);
  if (!catalog || !layouts || !can(me, 'playlists.manage') || !can(me, 'layouts.manage')) return null;
  return (
    <div className="card qs-card" data-testid="quickstart">
      <div className="row" style={{ marginBottom: 12 }}>
        <div className="grow">
          <h2 style={{ marginBottom: 4 }}>{t('quickstart.title')}</h2>
          <div className="muted">{t('quickstart.sub')}</div>
        </div>
      </div>
      <div className="qs-grid">
        {catalog.categories.map((c) => {
          const first = starterLayouts(catalog, c, 'landscape')[0];
          return (
            <button key={c.key} className="qs-tile" onClick={() => setPick(c)} aria-label={t('quickstart.choose', { name: tr(c.name, i18n.language) })}>
              <TemplateFrame layout={layouts.find((l) => l.id === first?.layoutId)} media={media} orientation="landscape" />
              <b>{tr(c.name, i18n.language)}</b>
              <span className="muted">{tr(c.description, i18n.language)}</span>
            </button>
          );
        })}
      </div>
      {pick && <QuickstartDialog category={pick} catalog={catalog} layouts={layouts} media={media} onClose={() => setPick(null)} />}
    </div>
  );
}

function QuickstartDialog({ category, catalog, layouts, media, onClose }: {
  category: TemplateCategory; catalog: TemplateCatalog; layouts: LayoutRow[]; media: MediaIndex; onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { data: me } = useMe();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [o, setO] = useState<Orientation>('landscape');
  const [busy, setBusy] = useState(false);
  const slides = starterLayouts(catalog, category, o);
  const publish = can(me, 'playlists.publish');
  const create = async () => {
    setBusy(true);
    try {
      const res = await api.post<{ playlistId: string; playlistName: string; revisionNo: number | null }>('/templates/quickstart', { category: category.key, orientation: o, publish });
      await Promise.all([qk.playlists, qk.layouts, qk.dashboard].map((k) => qc.invalidateQueries({ queryKey: k })));
      toast.show(t('quickstart.done', { name: res.playlistName }));
      navigate(`/playlists/${res.playlistId}`);
    } catch (e) {
      toast.error(e);
      setBusy(false);
    }
  };
  return (
    <Dialog wide title={t('quickstart.dialogTitle', { name: tr(category.name, i18n.language) })} onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>{t('common.cancel')}</button>
      <button className="btn primary" disabled={busy} onClick={() => void create()}><Icon name="check" />{t('quickstart.create')}</button>
    </>}>
      <div className="muted">{t('quickstart.explain', { count: slides.length })}</div>
      <div className="row">
        <span className="muted small">{t('templates.format')}</span>
        <div className="seg" role="group" aria-label={t('templates.format')}>
          {(['landscape', 'portrait'] as const).map((x) => (
            <button key={x} className={o === x ? 'on' : ''} aria-pressed={o === x} onClick={() => setO(x)}>
              {x === 'landscape' ? t('design.landscape') : t('design.portrait')}
            </button>
          ))}
        </div>
      </div>
      <div className={`qs-slides ${o}`}>
        {slides.map((s, i) => (
          <figure key={s.key}>
            <TemplateFrame layout={layouts.find((l) => l.id === s.layoutId)} media={media} orientation={o} />
            <figcaption>{i + 1}. {tr(s.name, i18n.language)}</figcaption>
          </figure>
        ))}
      </div>
      <div className="hint small">{publish ? t('quickstart.hintPublish') : t('quickstart.hintDraft')}</div>
    </Dialog>
  );
}
